/**
 * rw-yancha-cart-stamp-lock
 *
 * ヤンチャグッズのカート挙動:
 *   - スタンプ line item（`_im_session_id` を持ち `_im_product_id` を持たない）は
 *     数量変更・削除を不可にする（表示制御は cart-products.liquid 側）。
 *   - 関連する「本体」line item（同じ `_im_session_id` を持ち `_im_product_id` あり）が
 *     削除されたら、紐づくスタンプも同時に削除する（このスクリプトが担う）。
 *   - 本体の数量が変わったら、紐づくスタンプ数量も
 *     `_im_stamp_unit_qty`（本体1個あたり）× 本体数量 に同期する。
 *   - cart:update の itemCount はスタンプを除外した個数を返す
 *     （ヘッダー／ドロワーのカートバッジ用）。
 *   - カートページ表示時／ドロワー開放時に、`_im_design_expires_at` が無い
 *     本体だけ IM（App Proxy `/cart/check-designs`）へ問い合わせ、
 *     消えた／期限切れの本体＋スタンプを削除する。日付付き行は
 *     `_im_design_expires_at` の判定に任せる。IM 障害時は消さない（fail-open）。
 *
 * 仕組み:
 *   1. 本体の数量変更は `quantity-selector:update` を capture で横取りし、
 *      本体＋スタンプを `/cart/update.js` の1リクエストで同時更新する
 *      （標準の `/cart/change` → 後追い同期だと時間差が出るため）。
 *   2. それ以外の `cart:update` では孤立スタンプ削除と数量同期を後追いで行う。
 *
 * 参照: assets/component-cart-items.js / assets/cart-icon.js / assets/events.js
 */
(function () {
  if (window.__rwYanchaCartStampLockInit) return;
  window.__rwYanchaCartStampLockInit = true;

  var SESSION_KEY = '_im_session_id';
  var PRODUCT_KEY = '_im_product_id';
  // 本体1個あたりのスタンプ数。バックエンド（cartBuilder / mypage）が付与する。
  var UNIT_QTY_KEY = '_im_stamp_unit_qty';
  // 編集後のデザイン line item が持つ「編集元デザインのセッションID」。
  // バックエンド（cartBuilder）が edit_design 時に付与する。
  var SOURCE_SESSION_KEY = '_im_source_session_id';
  var EXPIRES_AT_KEY = '_im_design_expires_at';
  var EXPIRY_NOTICE_KEY = 'rw-yancha-design-expired';
  var EXPIRED_BANNER_SELECTOR = '[data-rw-design-expiry-banner]';
  var IM_CHECK_CACHE_KEY = 'rw-yancha-im-design-check';
  var IM_CHECK_CACHE_MS = 60 * 1000;
  var DEFAULT_PROXY_PATH = '/apps/yancha';
  var CART_UPDATE_EVENT = 'cart:update';
  var QUANTITY_SELECTOR_UPDATE_EVENT = 'quantity-selector:update';
  var DIALOG_OPEN_EVENT = 'dialog:open';
  var SOURCE = 'rw-yancha-stamp-lock';

  var processing = false;
  var processingSuperseded = false;
  var processingExpired = false;
  var processingImCheck = false;
  var syncingQuantity = false;
  var timer = null;
  var quantitySyncTimer = null;
  // property 未付与のレガシー行向け: session + variant ごとの unit を記憶する
  var unitQtyCache = {};

  function routes() {
    return (window.Theme && window.Theme.routes) || {};
  }

  function cartJsUrl() {
    var base = routes().cart_url || '/cart';
    return base + '.js';
  }

  function cartUpdateUrl() {
    return routes().cart_update_url || '/cart/update';
  }

  function getCartSectionIds() {
    var ids = {};
    document
      .querySelectorAll('cart-items-component[data-section-id]')
      .forEach(function (el) {
        if (el.dataset.sectionId) ids[el.dataset.sectionId] = true;
      });
    return Object.keys(ids);
  }

  function stampCacheKey(session, variantId) {
    return session + '\0' + String(variantId);
  }

  function parsePositiveInt(value) {
    var n = parseInt(value, 10);
    return n > 0 ? n : 0;
  }

  /**
   * スタンプ1商品あたり数量を解決する。
   * 優先順位: property → メモリキャッシュ → 現在数量からの推定（割り切れるときのみ）
   */
  function resolveStampUnitQty(item, mainQty) {
    var props = item.properties || {};
    var session = props[SESSION_KEY];
    if (!session) return 0;

    var cacheKey = stampCacheKey(session, item.variant_id);
    var fromProp = parsePositiveInt(props[UNIT_QTY_KEY]);
    if (fromProp) {
      unitQtyCache[cacheKey] = fromProp;
      return fromProp;
    }

    if (unitQtyCache[cacheKey]) {
      return unitQtyCache[cacheKey];
    }

    if (mainQty > 0 && item.quantity > 0 && item.quantity % mainQty === 0) {
      var inferred = item.quantity / mainQty;
      unitQtyCache[cacheKey] = inferred;
      return inferred;
    }

    return 0;
  }

  /**
   * 孤立スタンプ削除 + 本体数量に応じたスタンプ数量同期の updates を返す。
   * key → quantity（0 は削除）
   */
  function findStampReconcileUpdates(cart) {
    var items = (cart && cart.items) || [];
    var mainQtyBySession = {};

    items.forEach(function (item) {
      var props = item.properties || {};
      if (props[PRODUCT_KEY] && props[SESSION_KEY]) {
        var sid = props[SESSION_KEY];
        mainQtyBySession[sid] = (mainQtyBySession[sid] || 0) + item.quantity;
      }
    });

    var updates = {};

    items.forEach(function (item) {
      var props = item.properties || {};
      var session = props[SESSION_KEY];
      // スタンプ = セッションIDあり / 本体IDなし
      if (!session || props[PRODUCT_KEY]) return;

      var mainQty = mainQtyBySession[session] || 0;
      if (!mainQty) {
        updates[item.key] = 0;
        return;
      }

      var unit = resolveStampUnitQty(item, mainQty);
      if (!unit) return;

      var expected = unit * mainQty;
      if (item.quantity !== expected) {
        updates[item.key] = expected;
      }
    });

    return updates;
  }

  /**
   * カートバッジ用の個数。スタンプ line item は除外する。
   * （本体: `_im_product_id` あり / スタンプ: `_im_session_id` のみ）
   */
  function visibleItemCount(cart) {
    var items = (cart && cart.items) || [];
    var count = 0;
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (props[SESSION_KEY] && !props[PRODUCT_KEY]) continue;
      count += items[i].quantity || 0;
    }
    return count;
  }

  function dispatchCartUpdate(updatedCart) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: updatedCart,
          data: {
            source: SOURCE,
            itemCount: visibleItemCount(updatedCart),
            sections: updatedCart.sections,
          },
        },
      })
    );
  }

  function postCartUpdates(updates) {
    return fetch(cartUpdateUrl() + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: updates,
        sections: getCartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then(function (res) {
      return res.json();
    });
  }

  function reconcileStampLines() {
    if (processing) return;
    processing = true;

    fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var updates = findStampReconcileUpdates(cart);
        if (!Object.keys(updates).length) {
          processing = false;
          return null;
        }

        return postCartUpdates(updates);
      })
      .then(function (updatedCart) {
        processing = false;
        if (!updatedCart) return;

        // 各カートコンポーネント（cart-items / cart-icon 等）を再描画させる。
        // source を付けることで本スクリプト自身の再処理ループを防ぐ。
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        processing = false;
        console.error('[rw-yancha-cart-stamp-lock] stamp reconcile failed:', err);
      });
  }

  function scheduleCleanup() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcileStampLines, 80);
  }

  /**
   * 本体＋紐づくスタンプを1リクエストで同時削除する。
   * 標準の本体のみ削除＋後追い掃除だと時間差が出るため、こちらで一括処理する。
   */
  var removingGroup = false;

  function removeDesignGroup(session, clickedButton) {
    if (removingGroup || !session) return;
    removingGroup = true;

    // 対象セッションの行（本体＋スタンプ）を全コンポーネントから収集
    var rows = Array.prototype.slice.call(
      document.querySelectorAll('[data-im-session-id="' + session + '"]')
    );

    var updates = {};
    rows.forEach(function (row) {
      var key = row.getAttribute('data-key');
      if (key) updates[key] = 0;
    });

    if (!Object.keys(updates).length) {
      removingGroup = false;
      return;
    }

    // 同時に消えるようフェードアニメーションを付与（テーマの removeRow を流用）
    rows.forEach(function (row) {
      row.style.setProperty('--row-height', row.clientHeight + 'px');
      row.classList.add('removing');
    });
    if (clickedButton) clickedButton.disabled = true;

    postCartUpdates(updates)
      .then(function (updatedCart) {
        removingGroup = false;
        if (!updatedCart) return;
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        removingGroup = false;
        rows.forEach(function (row) {
          row.classList.remove('removing');
        });
        if (clickedButton) clickedButton.disabled = false;
        console.error('[rw-yancha-cart-stamp-lock] group remove failed:', err);
      });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var btn = target.closest('[data-rw-remove-group]');
    if (!btn) return;
    event.preventDefault();
    removeDesignGroup(btn.getAttribute('data-rw-session-id'), btn);
  });

  function uniqueRowsByKey(selector) {
    var seen = {};
    var rows = [];
    Array.prototype.forEach.call(document.querySelectorAll(selector), function (row) {
      var key = row.getAttribute('data-key');
      if (!key || seen[key]) return;
      seen[key] = true;
      rows.push(row);
    });
    return rows;
  }

  function readRowQuantity(row, usePrevious) {
    var input = row.querySelector('input[data-cart-line]');
    if (input) {
      // 数量セレクタはイベント発火時点で value が新数量になっているため、
      // 変更前の数量は defaultValue を使う。
      if (usePrevious) {
        return (
          parsePositiveInt(input.defaultValue) ||
          parsePositiveInt(input.getAttribute('data-cart-quantity')) ||
          parsePositiveInt(input.value)
        );
      }
      return (
        parsePositiveInt(input.value) ||
        parsePositiveInt(input.defaultValue) ||
        parsePositiveInt(input.getAttribute('data-cart-quantity'))
      );
    }
    var fixed = row.querySelector('.cart-items__quantity-fixed');
    return parsePositiveInt(fixed && fixed.textContent);
  }

  function resolveStampUnitFromRow(stampRow, session, oldMainQty) {
    var fromAttr = parsePositiveInt(stampRow.getAttribute('data-im-stamp-unit-qty'));
    if (fromAttr) {
      unitQtyCache[stampCacheKey(session, stampRow.getAttribute('data-key') || '')] = fromAttr;
      return fromAttr;
    }

    var cacheKey = stampCacheKey(session, stampRow.getAttribute('data-key') || '');
    if (unitQtyCache[cacheKey]) return unitQtyCache[cacheKey];

    var stampQty = readRowQuantity(stampRow);
    if (oldMainQty > 0 && stampQty > 0 && stampQty % oldMainQty === 0) {
      var inferred = stampQty / oldMainQty;
      unitQtyCache[cacheKey] = inferred;
      return inferred;
    }

    return 0;
  }

  function setCartItemsDisabled(disabled) {
    Array.prototype.forEach.call(document.querySelectorAll('cart-items-component'), function (el) {
      if (disabled) el.classList.add('cart-items-disabled');
      else el.classList.remove('cart-items-disabled');
    });
  }

  /**
   * 本体数量変更をスタンプと同時に `/cart/update` する。
   * 標準の `/cart/change`（本体のみ）＋後追い同期だと時間差が出るため。
   */
  function syncDesignGroupQuantity(mainRow, newMainQty) {
    var session = mainRow.getAttribute('data-im-session-id');
    var mainKey = mainRow.getAttribute('data-key');
    if (!session || !mainKey || syncingQuantity) return;

    if (newMainQty <= 0) {
      removeDesignGroup(session, null);
      return;
    }

    var oldMainTotal = 0;
    var newMainTotal = 0;
    var updates = {};

    uniqueRowsByKey('tr[data-im-design-main][data-im-session-id="' + session + '"]').forEach(function (row) {
      var key = row.getAttribute('data-key');
      if (!key) return;

      if (key === mainKey) {
        oldMainTotal += readRowQuantity(row, true) || newMainQty;
        newMainTotal += newMainQty;
        updates[key] = newMainQty;
      } else {
        var qty = readRowQuantity(row, false);
        oldMainTotal += qty;
        newMainTotal += qty;
      }
    });

    if (!newMainTotal) {
      removeDesignGroup(session, null);
      return;
    }

    uniqueRowsByKey('tr[data-im-stamp][data-im-session-id="' + session + '"]').forEach(function (row) {
      var key = row.getAttribute('data-key');
      if (!key) return;

      var unit = resolveStampUnitFromRow(row, session, oldMainTotal);
      if (!unit) return;

      updates[key] = unit * newMainTotal;
    });

    syncingQuantity = true;
    setCartItemsDisabled(true);

    postCartUpdates(updates)
      .then(function (updatedCart) {
        syncingQuantity = false;
        setCartItemsDisabled(false);
        if (!updatedCart || updatedCart.errors) {
          console.error('[rw-yancha-cart-stamp-lock] quantity sync failed:', updatedCart && updatedCart.errors);
          scheduleCleanup();
          return;
        }
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        syncingQuantity = false;
        setCartItemsDisabled(false);
        console.error('[rw-yancha-cart-stamp-lock] quantity sync failed:', err);
        scheduleCleanup();
      });
  }

  /**
   * 本体の数量セレクタ変更を横取りし、スタンプと同時更新する。
   * capture + stopImmediatePropagation で cart-items の `/cart/change` を抑止。
   */
  function onQuantitySelectorUpdate(event) {
    var detail = (event && event.detail) || {};
    var line = detail.cartLine;
    var quantity = detail.quantity;
    if (!line && line !== 0) return;

    var mainRow = document.querySelector(
      'tr[data-im-design-main][data-line-index="' + line + '"]'
    );
    if (!mainRow) return;

    event.stopImmediatePropagation();

    if (quantitySyncTimer) clearTimeout(quantitySyncTimer);
    quantitySyncTimer = setTimeout(function () {
      // 連打時は最終的な input 値を使う（デバウンス中に value が進むため）
      var input = mainRow.querySelector('input[data-cart-line]');
      var qty = input ? parsePositiveInt(input.value) : parsePositiveInt(quantity);
      syncDesignGroupQuantity(mainRow, qty || 0);
    }, 300);
  }

  document.addEventListener(QUANTITY_SELECTOR_UPDATE_EVENT, onQuantitySelectorUpdate, true);

  /**
   * 「デザインを変更する」押下時の処理。
   * 編集中も元のデザインはカートに残したいので、ここでは削除しない。
   * デザイン編集画面（App Proxy 経由で 302 リダイレクト）へ即遷移するだけ。
   * 編集URLには元デザインのセッションID（sourceSessionId）が含まれており、
   * 編集完了後にカートへ追加される新デザインの line item には
   * バックエンドが `_im_source_session_id` を付与する。
   * 実際の元デザイン削除は reconcileSupersededDesigns() がカート状態から行う。
   */
  var redirectingEdit = false;

  function redirectDesignEdit(editUrl, clickedButton) {
    if (redirectingEdit || !editUrl) return;
    redirectingEdit = true;
    if (clickedButton) clickedButton.disabled = true;
    window.location.href = editUrl;
  }

  /**
   * 編集から戻ってきたときの後始末。
   * カート内に「`_im_source_session_id` を持つ（＝編集後の）デザイン」があり、
   * その編集元セッションがまだカートに残っていれば、元デザイン
   * （本体＋紐づくスタンプ）をまとめて削除する。
   * カート状態だけで完結するため、localStorage 等の状態保持は不要。
   */
  function reconcileSupersededDesigns() {
    if (processingSuperseded) return;
    processingSuperseded = true;

    fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];

        // 編集後デザインが参照している「編集元セッションID」の集合
        var supersededSessions = {};
        items.forEach(function (item) {
          var props = item.properties || {};
          var source = props[SOURCE_SESSION_KEY];
          if (source) supersededSessions[source] = true;
        });

        if (!Object.keys(supersededSessions).length) {
          processingSuperseded = false;
          return null;
        }

        // 編集元セッション（本体＋スタンプ）の line item を削除対象に集める
        var updates = {};
        items.forEach(function (item) {
          var props = item.properties || {};
          var session = props[SESSION_KEY];
          if (session && supersededSessions[session]) {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) {
          processingSuperseded = false;
          return null;
        }

        return postCartUpdates(updates);
      })
      .then(function (updatedCart) {
        processingSuperseded = false;
        if (!updatedCart) return;
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        processingSuperseded = false;
        console.error('[rw-yancha-cart-stamp-lock] superseded reconcile failed:', err);
      });
  }

  function isExpiredExpiresAt(value, nowMs) {
    if (!value) return false;
    var ms = Date.parse(String(value).trim());
    if (isNaN(ms)) return false;
    return nowMs > ms;
  }

  function findExpiredDesignUpdates(cart) {
    var items = (cart && cart.items) || [];
    var nowMs = Date.now();
    var expiredSessions = {};

    items.forEach(function (item) {
      var props = item.properties || {};
      if (!props[PRODUCT_KEY] || !props[SESSION_KEY]) return;
      if (isExpiredExpiresAt(props[EXPIRES_AT_KEY], nowMs)) {
        expiredSessions[props[SESSION_KEY]] = true;
      }
    });

    if (!Object.keys(expiredSessions).length) return {};

    var updates = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      var session = props[SESSION_KEY];
      if (session && expiredSessions[session]) {
        updates[item.key] = 0;
      }
    });
    return updates;
  }

  function showExpiryBannerIfNeeded() {
    var flagged = false;
    try {
      flagged = sessionStorage.getItem(EXPIRY_NOTICE_KEY) === '1';
    } catch (err) {
      flagged = false;
    }
    if (!flagged) return;
    document.querySelectorAll(EXPIRED_BANNER_SELECTOR).forEach(function (el) {
      el.removeAttribute('hidden');
    });
  }

  function clearExpiryNotice() {
    try {
      sessionStorage.removeItem(EXPIRY_NOTICE_KEY);
    } catch (err) {
      // private mode 等では無視
    }
  }

  function markExpiryNotice() {
    try {
      sessionStorage.setItem(EXPIRY_NOTICE_KEY, '1');
    } catch (err) {
      // private mode 等では無視
    }
    showExpiryBannerIfNeeded();
  }

  function reconcileExpiredDesigns() {
    if (processingExpired) return;
    processingExpired = true;

    fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var updates = findExpiredDesignUpdates(cart);
        if (!Object.keys(updates).length) {
          processingExpired = false;
          return null;
        }
        markExpiryNotice();
        return postCartUpdates(updates);
      })
      .then(function (updatedCart) {
        processingExpired = false;
        if (!updatedCart) return;
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        processingExpired = false;
        console.error('[rw-yancha-cart-stamp-lock] expired design reconcile failed:', err);
      });
  }

  function proxyPath() {
    var el = document.querySelector('[data-rw-design-expiry-banner][data-app-proxy-path]');
    return (el && el.getAttribute('data-app-proxy-path')) || DEFAULT_PROXY_PATH;
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function collectImProductIds(cart) {
    var seen = {};
    var ids = [];
    ((cart && cart.items) || []).forEach(function (item) {
      var props = item.properties || {};
      if (!props[PRODUCT_KEY] || !props[SESSION_KEY]) return;
      // 期限プロパティがある行は reconcileExpiredDesigns で足りる。
      // IM 確認は日付のない本体（永続保存・レガシー）だけにする。
      if (props[EXPIRES_AT_KEY]) return;
      var id = parsePositiveInt(props[PRODUCT_KEY]);
      if (!id || seen[id]) return;
      seen[id] = true;
      ids.push(id);
    });
    return ids;
  }

  function findMissingImDesignUpdates(cart, missingIds) {
    var missing = {};
    missingIds.forEach(function (id) {
      missing[String(id)] = true;
    });

    var items = (cart && cart.items) || [];
    var expiredSessions = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      if (!props[PRODUCT_KEY] || !props[SESSION_KEY]) return;
      if (missing[String(parsePositiveInt(props[PRODUCT_KEY]))]) {
        expiredSessions[props[SESSION_KEY]] = true;
      }
    });

    if (!Object.keys(expiredSessions).length) return {};

    var updates = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      var session = props[SESSION_KEY];
      if (session && expiredSessions[session]) {
        updates[item.key] = 0;
      }
    });
    return updates;
  }

  function readImCheckCache(ids) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(IM_CHECK_CACHE_KEY) || 'null');
      if (!cached || !cached.at || Date.now() - cached.at >= IM_CHECK_CACHE_MS) return null;
      var prev = (cached.ids || []).slice().sort().join(',');
      var next = ids.slice().sort().join(',');
      if (prev !== next) return null;
      return Array.isArray(cached.missing) ? cached.missing : [];
    } catch (err) {
      return null;
    }
  }

  function writeImCheckCache(ids, missing) {
    try {
      sessionStorage.setItem(
        IM_CHECK_CACHE_KEY,
        JSON.stringify({ at: Date.now(), ids: ids, missing: missing })
      );
    } catch (err) {
      // private mode 等では無視
    }
  }

  function fetchMissingImProductIds(ids) {
    var cached = readImCheckCache(ids);
    if (cached) return Promise.resolve(cached);

    return fetch(proxyPath() + '/cart/check-designs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ imProductIds: ids }),
    }).then(function (res) {
      if (!res.ok) throw new Error('check-designs HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      var missing = (data && data.missingProductIds) || [];
      writeImCheckCache(ids, missing);
      return missing;
    });
  }

  function reconcileMissingImDesigns() {
    if (processingImCheck) return;
    processingImCheck = true;

    fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var ids = collectImProductIds(cart);
        if (!ids.length) {
          processingImCheck = false;
          return null;
        }
        return fetchMissingImProductIds(ids).then(function (missing) {
          if (!missing.length) {
            processingImCheck = false;
            return null;
          }
          var updates = findMissingImDesignUpdates(cart, missing);
          if (!Object.keys(updates).length) {
            processingImCheck = false;
            return null;
          }
          markExpiryNotice();
          return postCartUpdates(updates);
        });
      })
      .then(function (updatedCart) {
        processingImCheck = false;
        if (!updatedCart) return;
        dispatchCartUpdate(updatedCart);
      })
      .catch(function (err) {
        processingImCheck = false;
        console.error('[rw-yancha-cart-stamp-lock] IM design check failed:', err);
      });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var editBtn = target.closest('[data-rw-design-edit]');
    if (!editBtn) return;
    event.preventDefault();
    redirectDesignEdit(editBtn.getAttribute('data-rw-edit-url'), editBtn);
  });

  document.addEventListener(CART_UPDATE_EVENT, function (event) {
    showExpiryBannerIfNeeded();
    // 自分が発火させた更新は無視（無限ループ防止）
    if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) {
      return;
    }
    // 同時数量同期中の後続イベントは reconcile 不要
    if (syncingQuantity) return;
    scheduleCleanup();
    reconcileSupersededDesigns();
    reconcileExpiredDesigns();
  });

  // 孤立スタンプ掃除・数量同期と、編集から戻った直後の元デザイン削除、
  // 期限切れデザイン削除に備え、初期表示時にも一度チェックする。
  // 期限切れバナーは削除直後の remorph（cart:update）だけで出し、
  // リロード後に sessionStorage が残って出続けるのは防ぐ。
  // IM 存在確認はカートページ表示時のみ。ドロワーは開いたときに確認する。
  function runInitialChecks() {
    clearExpiryNotice();
    scheduleCleanup();
    reconcileSupersededDesigns();
    reconcileExpiredDesigns();
    if (isCartPage()) reconcileMissingImDesigns();
  }

  document.addEventListener(
    DIALOG_OPEN_EVENT,
    function (event) {
      var target = event.target;
      if (!target || !target.closest || !target.closest('cart-drawer-component')) return;
      reconcileMissingImDesigns();
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitialChecks);
  } else {
    runInitialChecks();
  }
})();
